import React, { useMemo } from 'react';
import { FiUser, FiMail, FiMapPin, FiPhone, FiGlobe, FiLinkedin, FiTwitter, FiGithub, FiYoutube, FiBriefcase, FiAward, FiBookOpen, FiMessageSquare } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import MasonryLayout from './MasonryLayout';

// Helper to ensure links are absolute
const getSafeLink = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // If it contains a domain, add https
  if (url.includes('.com') || url.includes('.io') || url.includes('.org') || url.includes('.net')) {
    return `https://${url}`;
  }
  return `https://${url}`;
};

// Helper to get social platform URL
const getSocialUrl = (platform, value) => {
  if (!value) return '';
  
  // If already a full URL, use it
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  // Remove any leading @ or /
  const cleanValue = value.replace(/^[@\/]+/, '');

  // Map platform to base URL
  const platformUrls = {
    linkedin: 'https://linkedin.com/in/',
    x: 'https://x.com/',
    twitter: 'https://twitter.com/',
    github: 'https://github.com/',
    youtube: 'https://youtube.com/'
  };

  // If it contains the platform domain, add protocol
  if (cleanValue.includes('linkedin.com') || cleanValue.includes('x.com') || 
      cleanValue.includes('twitter.com') || cleanValue.includes('github.com') || 
      cleanValue.includes('youtube.com')) {
    return `https://${cleanValue}`;
  }

  // Otherwise, construct URL with username
  const baseUrl = platformUrls[platform.toLowerCase()];
  return baseUrl ? `${baseUrl}${cleanValue}` : `https://${cleanValue}`;
};

// Styled Card Wrapper
const Card = ({ children, bioData, className = "" }) => (
  <div
    className={`overflow-hidden transition-all duration-300 hover:scale-[1.01] ${className}`}
    style={{
      backgroundColor: bioData.colors.cardBackground,
      borderColor: bioData.colors.cardBorder,
      color: bioData.colors.text,
      ...bioData.cardStyle
    }}
  >
    {children}
  </div>
);

// Section Header Helper
const SectionTitle = ({ icon, title, color }) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b" style={{ borderColor: `${color}30` }}>
    <SafeIcon icon={icon} className="text-lg" style={{ color }} />
    <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{title}</h3>
  </div>
);

function PreviewCard({ bioData, isPreviewMode = false }) {
  const accent = bioData.colors.accent;

  // Memoize cards to prevent unnecessary re-calculations during parent updates
  const cards = useMemo(() => [
    // 1. Avatar Card
    <Card bioData={bioData} key="avatar" className="flex flex-col items-center justify-center p-8 text-center">
      <div className="w-32 h-32 rounded-full border-4 p-1 mb-4 relative" style={{ borderColor: accent }}>
        <div className="w-full h-full rounded-full overflow-hidden bg-black/20">
          {bioData.avatar ? (
            <img src={bioData.avatar} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl opacity-20"><FiUser /></div>
          )}
        </div>
      </div>
    </Card>,

    // 2. Header / Name Card
    <Card bioData={bioData} key="header">
      <h1 className={`text-4xl font-bold mb-2 leading-tight ${bioData.fontEffect?.includes('metallic') ? bioData.fontEffect.replace('metallic-', 'font-effect-') : ''}`}>
        {bioData.name}
      </h1>
      <h2 className="text-lg font-medium opacity-80 mb-4" style={{ color: accent }}>{bioData.title}</h2>
      <div className={`flex gap-2 flex-wrap ${bioData.cardStyle.textAlign === 'center' ? 'justify-center' : bioData.cardStyle.textAlign === 'right' ? 'justify-end' : 'justify-start'}`}>
        {bioData.socials.linkedin && (
          <a 
            href={getSocialUrl('linkedin', bioData.socials.linkedin)} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors" 
            title="LinkedIn"
          >
            <SafeIcon icon={FiLinkedin} />
          </a>
        )}
        {bioData.socials.x && (
          <a 
            href={getSocialUrl('x', bioData.socials.x)} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors" 
            title="X (Twitter)"
          >
            <SafeIcon icon={FiTwitter} />
          </a>
        )}
        {bioData.socials.github && (
          <a 
            href={getSocialUrl('github', bioData.socials.github)} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors" 
            title="GitHub"
          >
            <SafeIcon icon={FiGithub} />
          </a>
        )}
        {bioData.socials.youtube && (
          <a 
            href={getSocialUrl('youtube', bioData.socials.youtube)} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors" 
            title="YouTube"
          >
            <SafeIcon icon={FiYoutube} />
          </a>
        )}
      </div>
    </Card>,

    // 3. About / Summary
    <Card bioData={bioData} key="about">
      <SectionTitle icon={FiUser} title="Profile" color={accent} />
      <p className="opacity-80 leading-relaxed whitespace-pre-wrap text-sm">{bioData.about}</p>
    </Card>,

    // 4. Contact Info
    <Card bioData={bioData} key="contact">
      <SectionTitle icon={FiPhone} title="Contact" color={accent} />
      <div className="space-y-3 text-sm">
        {bioData.email && (
          <div className={`flex items-center gap-3 ${bioData.cardStyle.textAlign === 'center' ? 'justify-center' : bioData.cardStyle.textAlign === 'right' ? 'justify-end flex-row-reverse' : ''}`}>
            <SafeIcon icon={FiMail} className="opacity-50" />
            <a href={`mailto:${bioData.email}`} className="opacity-90 hover:text-blue-400 transition-colors">{bioData.email}</a>
          </div>
        )}
        {bioData.phone && (
          <div className={`flex items-center gap-3 ${bioData.cardStyle.textAlign === 'center' ? 'justify-center' : bioData.cardStyle.textAlign === 'right' ? 'justify-end flex-row-reverse' : ''}`}>
            <SafeIcon icon={FiPhone} className="opacity-50" />
            <a href={`tel:${bioData.phone}`} className="opacity-90 hover:text-blue-400 transition-colors">{bioData.phone}</a>
          </div>
        )}
        {bioData.location && (
          <div className={`flex items-center gap-3 ${bioData.cardStyle.textAlign === 'center' ? 'justify-center' : bioData.cardStyle.textAlign === 'right' ? 'justify-end flex-row-reverse' : ''}`}>
            <SafeIcon icon={FiMapPin} className="opacity-50" />
            <span className="opacity-90">{bioData.location}</span>
          </div>
        )}
        {bioData.website && (
          <div className={`flex items-center gap-3 ${bioData.cardStyle.textAlign === 'center' ? 'justify-center' : bioData.cardStyle.textAlign === 'right' ? 'justify-end flex-row-reverse' : ''}`}>
            <SafeIcon icon={FiGlobe} className="opacity-50" />
            <a href={getSafeLink(bioData.website)} target="_blank" rel="noopener noreferrer" className="opacity-90 hover:text-blue-400 transition-colors">{bioData.website}</a>
          </div>
        )}
      </div>
    </Card>,

    // 5. Experience
    <Card bioData={bioData} key="experience">
      <SectionTitle icon={FiBriefcase} title="Experience" color={accent} />
      <div className="space-y-6">
        {bioData.experience.map((exp, i) => (
          <div key={i} className="relative pl-4 border-l-2" style={{ borderColor: `${accent}30` }}>
            <div className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accent }}></div>
            <div className="flex flex-col mb-1">
              <span className="text-xs font-bold opacity-60 uppercase tracking-wider">{exp.year}</span>
              <h4 className="font-bold text-lg">{exp.role}</h4>
              <span className="text-sm font-medium opacity-80 mb-2">{exp.company}</span>
            </div>
            <p className="text-xs opacity-70 leading-relaxed">{exp.desc}</p>
          </div>
        ))}
      </div>
    </Card>,

    // 6. Education
    <Card bioData={bioData} key="education">
      <SectionTitle icon={FiBookOpen} title="Education" color={accent} />
      <div className="space-y-4">
        {bioData.education.map((edu, i) => (
          <div key={i} className="bg-white/5 p-3 rounded-lg">
            <span className="block text-xs font-bold opacity-50 mb-1">{edu.year}</span>
            <h4 className="font-bold text-sm">{edu.degree}</h4>
            <span className="text-xs opacity-70">{edu.school}</span>
          </div>
        ))}
      </div>
    </Card>,

    // 7. Skills
    <Card bioData={bioData} key="skills">
      <SectionTitle icon={FiAward} title="Expertise" color={accent} />
      <div className={`flex flex-wrap gap-2 ${bioData.cardStyle.textAlign === 'center' ? 'justify-center' : bioData.cardStyle.textAlign === 'right' ? 'justify-end' : ''}`}>
        {bioData.skills.map((skill, i) => (
          <span
            key={i}
            className="px-3 py-1 rounded-full text-xs font-bold bg-white/5 border border-white/10"
            style={{ color: bioData.colors.text }}
          >
            {skill}
          </span>
        ))}
      </div>
    </Card>,

    // 8. Languages
    <Card bioData={bioData} key="languages">
      <SectionTitle icon={FiGlobe} title="Languages" color={accent} />
      <ul className="space-y-2">
        {bioData.languages.map((lang, i) => (
          <li key={i} className="flex items-center justify-between text-sm py-1 border-b border-white/5 last:border-0">
            <span>{lang}</span>
            <SafeIcon icon={FiAward} className="opacity-30 text-xs" />
          </li>
        ))}
      </ul>
    </Card>,

    // 9. References
    <Card bioData={bioData} key="references">
      <SectionTitle icon={FiMessageSquare} title="References" color={accent} />
      <div className="space-y-4">
        {bioData.references.map((ref, i) => (
          <div key={i} className="text-sm">
            <div className="font-bold">{ref.name}</div>
            <div className="opacity-60 text-xs mb-1">{ref.role}, {ref.company}</div>
            <div className="opacity-80 text-xs">{ref.contact}</div>
          </div>
        ))}
      </div>
    </Card>
  ], [bioData, accent]);

  return (
    <div
      className="w-full min-h-screen p-8 transition-colors duration-500"
      style={{
        background: bioData.colors.background,
        fontFamily: bioData.fontFamily || 'inherit'
      }}
    >
      <MasonryLayout>
        {cards}
      </MasonryLayout>
    </div>
  );
}

export default PreviewCard;